import { useEmployee } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { useMailConfiguration } from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.hooks';
import { SenderType } from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.types';
import { useMailSend } from '@/core/domain/mail/MailControler/MailControler.hooks';
import {
  ContentType,
  MailMessageType,
  PurchaseMailSendingForm,
  PurchaseMailSendingRequestParams,
  SendPurchaseEnum,
} from '@/core/domain/mail/MailControler/MailControler.types';
import { getUserFromLocalStorage } from '@/utils/user';
import { Alert, Button, Form, Modal, ModalProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import PurchaseSendEmailForm from './PurchaseSendEmailForm';
import { Purchase } from '@/core/domain/purchase/Purchase.types';
import mailBase64ToUrl from '@/utils/functions/mailBase64ToUrl';
import moment from 'moment';
import { MailTypeEnum } from '@/core/domain/mail/MailHistory/MailHistory.types';
import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: () => void;
  purchase?: Purchase;
};

const PurchaseSendEmailModal: React.FC<Props> = ({
  open,
  onCancel,
  purchase,
}) => {
  const { employeeId, userId } = getUserFromLocalStorage();
  const { data: employee } = useEmployee(Number(employeeId));
  const intl = useIntl();

  const companyDetail = useCompany(purchase?.companyId, open);
  const companyName = companyDetail.data?.name || '';

  const [form] = Form.useForm<PurchaseMailSendingForm>();

  const mailSend = useMailSend();
  const [uploading, setUploading] = useState(false);

  const { data: mailConfigurationData, dataUpdatedAt } = useMailConfiguration(
    {
      sender: SenderType.user,
      senderId: Number(userId),
    },
    open,
  );

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  useEffect(() => {
    if (mailConfigurationData) {
      form.setFieldsValue({ message: mailConfigurationData.signature });
    }
  }, [dataUpdatedAt, open]);

  async function handleSubmit() {
    try {
      let newMessage = '';
      const { attachment, sendPurchase, ...values } =
        await form.validateFields();

      if (!employee) return;

      newMessage = await mailBase64ToUrl(values.message, setUploading);

      let obj: PurchaseMailSendingRequestParams = {
        ...values,
        message: newMessage,
        mailType: MailTypeEnum.PURCHASE,
        messageType: MailMessageType.Html,
        senderId: String(userId),
        sender: SenderType.user,
        userId: String(userId),
        from: mailConfigurationData?.mail,
        fromName: `${companyName} - ${employee?.name}`,
      };

      if (sendPurchase !== SendPurchaseEnum.none) {
        obj = {
          ...obj,
          attachments: [
            {
              content: attachment,
              fileName: `compra_${moment().format('DD_MM_YYYY')}.pdf`,
              contentType: ContentType.pdf,
            },
          ],
        };
      }

      await mailSend.mutateAsync(obj);

      handleCancel();
    } catch {}
  }

  return (
    <Modal
      title={intl.formatMessage({
        id: 'purchases.sendEmail.modal.form.title',
      })}
      open={open}
      onCancel={handleCancel}
      width={1000}
      footer={[
        <Button type="primary" ghost onClick={handleCancel}>
          {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
        </Button>,
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={mailSend.isLoading || uploading}
          disabled={!mailConfigurationData}
        >
          Enviar
        </Button>,
      ]}
    >
      {!mailConfigurationData && (
        <Alert
          type="info"
          description="Necessário configurar os dados de e-mail"
          style={{ marginBottom: 24 }}
        />
      )}
      <PurchaseSendEmailForm form={form} purchase={purchase} />
    </Modal>
  );
};

export default PurchaseSendEmailModal;
